import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { FiCopy } from 'react-icons/fi'
import { HiOutlineVideoCamera } from 'react-icons/hi'
import { BiChevronDown, BiCopy } from 'react-icons/bi'
import { CaseDetailContext } from './CaseDetailContext'
import { resolvePortalOrigin } from '../../utils/scalePortalUrl'

// Where the parties' Zoom-gate page lives (Scale Mediation portal).
//
// This used to read the env var directly and fall back to http://localhost:3001.
// REACT_APP_* is inlined at BUILD time and the variable is not set in the
// deployed Vercel projects, so every /join link built off a UAT or production
// Case Manager pointed a party at localhost. Same resolver as the rest of the
// file now, which derives the right host when the env override is absent.

/**
 * OnboardingLinks — port of Scale's OnBoardingLinks. Four buttons in a row:
 *   - Plaintiff Onboarding Link  (dropdown if multiple plaintiff parties)
 *   - Defendant Onboarding Link  (dropdown if multiple defendant parties)
 *   - Copy Zoom Link             (copy to clipboard, with vanity URL if set)
 *   - Join Breakout Room         (open zoom link)
 *
 * The first two buttons render even when the party data isn't available
 * yet — they just disable so the layout stays stable, instead of
 * disappearing and shifting the header.
 */

function LinkButton({ label, disabled, onClick, hasDropdown, isOpen, onToggle }) {
    return (
        <button
            type="button"
            onClick={hasDropdown ? onToggle : onClick}
            disabled={disabled}
            className={`flex items-center justify-between gap-2 px-4 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'}`}
        >
            <div className="flex items-center gap-2">
                <BiCopy className="h-4 w-4 text-gray-500" />
                <span>{label}</span>
            </div>
            {hasDropdown && <BiChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
        </button>
    )
}

export default function OnboardingLinks({ onToast }) {
    const { caseData, plaintiffPartyLabel, defendantPartyLabel } = useContext(CaseDetailContext)
    const [isPlaintiffOpen, setIsPlaintiffOpen] = useState(false)
    const [isDefendantOpen, setIsDefendantOpen] = useState(false)
    const plaintiffRef = useRef(null)
    const defendantRef = useRef(null)

    useEffect(() => {
        const onClickOutside = (e) => {
            if (plaintiffRef.current && !plaintiffRef.current.contains(e.target)) setIsPlaintiffOpen(false)
            if (defendantRef.current && !defendantRef.current.contains(e.target)) setIsDefendantOpen(false)
        }
        document.addEventListener('mousedown', onClickOutside)
        return () => document.removeEventListener('mousedown', onClickOutside)
    }, [])

    // The onboarding-form page is hosted on Scale Portal, not case-manager.
    // window.location.origin would produce a broken link (case-manager's
    // domain doesn't serve the form). Origin resolution lives in
    // utils/scalePortalUrl, shared with the Booking Link page and the
    // mediator Booking Link tab so a fix to one reaches all three.
    const baseUrl = resolvePortalOrigin()

    const handleCopy = (link, isZoom = false) => {
        if (!link) {
            onToast?.({ type: 'error', message: 'Link not available yet for this case' })
            return
        }
        navigator.clipboard.writeText(link)
            .then(() => onToast?.({ type: 'success', message: `${isZoom ? 'Zoom' : 'Onboarding'} link copied` }))
            .catch(() => onToast?.({ type: 'error', message: 'Failed to copy link' }))
    }

    const links = useMemo(() => {
        if (!caseData) return { plaintiff: [], defendant: [] }
        const plaintiffParties = caseData.plaintiff?.additionalParties || []
        const defendantParties = caseData.defendant?.additionalParties || []
        const pid = caseData.plaintiff?.client_id || caseData.plaintiff?.clientId
        const did = caseData.defendant?.client_id || caseData.defendant?.clientId
        // The onboarding form resolves a CLIENT by uuid, so the link must carry
        // clients.uuid. An additional party also has a case_parties.uuid — a
        // different value — and falling back to it produced a link that looked
        // right but resolved to nothing. No fallback here, matching the
        // mediator portal: no client uuid means no link, which is visible,
        // rather than a broken one, which is not.
        const buildUrl = (uuid) => uuid ? `${baseUrl}/onboarding-form/${caseData.id}/${uuid}` : null
        return {
            plaintiff: pid ? [
                { label: `${plaintiffPartyLabel} - ${caseData.plaintiff.name || ''}`, url: buildUrl(caseData.plaintiff.uuid) },
                ...plaintiffParties.map((p) => ({
                    label: `${plaintiffPartyLabel} ${p.position || ''} - ${p.client?.name || p.name || ''}`,
                    url: buildUrl(p.client?.uuid),
                })),
            ] : [],
            defendant: did ? [
                { label: `${defendantPartyLabel} - ${caseData.defendant.name || ''}`, url: buildUrl(caseData.defendant.uuid) },
                ...defendantParties.map((p) => ({
                    label: `${defendantPartyLabel} ${p.position || ''} - ${p.client?.name || p.name || ''}`,
                    url: buildUrl(p.client?.uuid),
                })),
            ] : [],
        }
    }, [caseData, plaintiffPartyLabel, defendantPartyLabel, baseUrl])

    // Honor mediator's zoom_vanity_url if configured. When the case
    // has a signing request (flag baked into the case-detail
    // response by getCase on cm-be), the gate URL takes precedence —
    // copy button mirrors what parties get in their email.
    const zoomVanity = caseData?.mediatorSettings?.zoom_vanity_url || null
    const rawZoomLink = zoomVanity
        ? `${zoomVanity}/?case_id=${caseData?.id}`
        : caseData?.zoomLink || caseData?.zoom_link
    const hasSigningRequest = !!caseData?.hasSigningRequest
    const zoomGateLink = caseData?.id
        ? `${resolvePortalOrigin()}/join/${caseData.id}`
        : null
    const zoomLink = hasSigningRequest ? zoomGateLink : rawZoomLink

    const renderDropdownPanel = (items, refEl, isOpen, setIsOpen, partyLabel) => {
        if (!isOpen) return null
        if (items.length === 0) {
            return (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 p-3">
                    <p className="text-xs text-gray-500">No {partyLabel.toLowerCase()} link yet.</p>
                </div>
            )
        }
        return (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50">
                {items.map((it, i) => (
                    <button
                        key={i}
                        type="button"
                        onClick={() => { handleCopy(it.url); setIsOpen(false) }}
                        disabled={!it.url}
                        className="capitalize text-gray-700 w-full text-left px-4 py-2 hover:bg-gray-100 first:rounded-t-md last:rounded-b-md disabled:opacity-50"
                    >
                        {it.label}
                    </button>
                ))}
            </div>
        )
    }

    return (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-x-2 flex-wrap">
            {/* Plaintiff link button */}
            <div className="relative w-full sm:w-fit" ref={plaintiffRef}>
                {(() => {
                    const items = links.plaintiff
                    const noPlaintiff = items.length === 0
                    if (items.length <= 1) {
                        return (
                            <LinkButton
                                label={`${plaintiffPartyLabel} Onboarding Link`}
                                disabled={noPlaintiff || !items[0]?.url}
                                onClick={() => handleCopy(items[0]?.url)}
                            />
                        )
                    }
                    return (
                        <>
                            <LinkButton
                                label={`${plaintiffPartyLabel} Onboarding Link`}
                                hasDropdown
                                isOpen={isPlaintiffOpen}
                                onToggle={() => setIsPlaintiffOpen((v) => !v)}
                                disabled={false}
                            />
                            {renderDropdownPanel(items, plaintiffRef, isPlaintiffOpen, setIsPlaintiffOpen, plaintiffPartyLabel)}
                        </>
                    )
                })()}
            </div>

            {/* Defendant link button */}
            <div className="relative w-full sm:w-fit" ref={defendantRef}>
                {(() => {
                    const items = links.defendant
                    const noDefendant = items.length === 0
                    if (items.length <= 1) {
                        return (
                            <LinkButton
                                label={`${defendantPartyLabel} Onboarding Link`}
                                disabled={noDefendant || !items[0]?.url}
                                onClick={() => handleCopy(items[0]?.url)}
                            />
                        )
                    }
                    return (
                        <>
                            <LinkButton
                                label={`${defendantPartyLabel} Onboarding Link`}
                                hasDropdown
                                isOpen={isDefendantOpen}
                                onToggle={() => setIsDefendantOpen((v) => !v)}
                                disabled={false}
                            />
                            {renderDropdownPanel(items, defendantRef, isDefendantOpen, setIsDefendantOpen, defendantPartyLabel)}
                        </>
                    )
                })()}
            </div>

            {/* Copy Zoom Link */}
            <button
                type="button"
                disabled={!zoomLink}
                onClick={() => handleCopy(zoomLink, true)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md bg-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <FiCopy size={16} className="text-gray-500" />
                Copy Zoom Link
            </button>

            {/* Join Breakout Room */}
            <button
                type="button"
                disabled={!zoomLink}
                onClick={() => zoomLink && window.open(zoomLink, '_blank')}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <HiOutlineVideoCamera size={16} />
                Join Breakout Room
            </button>
        </div>
    )
}
